import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { translations, formatMinutesLong } from "@/i18n/translations";

const STORAGE_KEY = "bom_lang";
const SUPPORTED = ["en", "pl"];
const DEFAULT_LANG = "en";

const LanguageContext = createContext(null);

const lookup = (dict, key) =>
  key.split(".").reduce((obj, k) => (obj && obj[k] !== undefined ? obj[k] : undefined), dict);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED.includes(stored) ? stored : DEFAULT_LANG;
  });

  // Keep <html lang> in sync with the selected language and persist choice.
  useEffect(() => {
    document.documentElement.lang = lang;
    localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const setLang = useCallback((next) => {
    if (SUPPORTED.includes(next)) setLangState(next);
  }, []);

  /**
   * Translate by dot-path key with optional {var} interpolation.
   * Falls back to English, then to the key itself. Arrays (message pools)
   * are returned as-is.
   */
  const t = useCallback(
    (key, vars) => {
      let value = lookup(translations[lang], key);
      if (value === undefined) value = lookup(translations[DEFAULT_LANG], key);
      if (value === undefined) return key;
      if (typeof value === "string" && vars) {
        for (const [k, v] of Object.entries(vars)) {
          value = value.split(`{${k}}`).join(String(v));
        }
      }
      return value;
    },
    [lang]
  );

  const minutesLong = useCallback((n) => formatMinutesLong(n, lang), [lang]);

  const locale = lang === "pl" ? "pl-PL" : "en-US";

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, locale, minutesLong }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};
